import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
  Button,
  Image,
} from "react-bootstrap";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {
        "Content-Type": "application/json",
      };
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let data = null;
      let lastErr = null;

      // Try to fetch profile from each base URL until one works
      for (const base of bases) {
        try {
          const res = await fetch(`${base}/api/users/${id}`, { headers });
          if (!res.ok) {
            lastErr = new Error(`Failed to load user profile from ${base}`);
            continue;
          }
          data = await res.json();
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!data)
        throw (
          lastErr || new Error("Failed to load user profile from any endpoint")
        );

      const u = data.user || {};
      const mapped = {
        id: u.id || u._id || id,
        email: u.email || "—",
        name: u.name || "—",
        phone: u.phone || "—",
        address: u.address || "—",
        profileImage: u.profileImage || "",
        isProfileComplete: !!u.isProfileComplete,
        dateOfBirth: u.dateOfBirth || null,
        gender: u.gender || "—",
        isActive: u.isActive !== false,
        lastLogin: u.lastLogin || null,
        referralCode: u.referralCode || "—",
        referralPoints: u.referralPoints || 0,
        referralsCount: u.referralsCount || 0,
        subscription: u.subscription || "none",
        createdAt: u.createdAt || null,
        updatedAt: u.updatedAt || null,
      };
      setProfile(mapped);
      setLoading(false);
    } catch (e) {
      setError("Failed to load profile");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchProfile}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!profile) {
    return <div>No profile found</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <div className="me-3">
            {profile.profileImage ? (
              <Image
                src={profile.profileImage}
                roundedCircle
                width={60}
                height={60}
                alt={profile.name}
              />
            ) : (
              <div
                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 60, height: 60 }}
              >
                <FaUser size={30} />
              </div>
            )}
          </div>
          <div>
            <h1 className="mb-1">{profile.name}</h1>
            <p className="text-muted mb-0">User Profile • ID: {profile.id}</p>
          </div>
        </div>
        <div>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Personal Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Name:</strong> {profile.name}
                  </p>
                  <p>
                    <FaEnvelope className="me-2" />
                    {profile.email}
                  </p>
                  <p>
                    <FaPhone className="me-2" />
                    {profile.phone}
                  </p>
                  <p>
                    <FaMapMarkerAlt className="me-2" />
                    {profile.address}
                  </p>
                  <p>
                    <strong>Date of Birth:</strong>{" "}
                    {profile.dateOfBirth
                      ? new Date(profile.dateOfBirth).toLocaleDateString()
                      : "—"}
                  </p>
                  <p>
                    <strong>Gender:</strong> {profile.gender || "—"}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>User ID:</strong> {profile.id}
                  </p>
                  <p>
                    <strong>Last Login:</strong>{" "}
                    {profile.lastLogin
                      ? new Date(profile.lastLogin).toLocaleString()
                      : "—"}
                  </p>
                  <p>
                    <strong>Profile Complete:</strong>{" "}
                    {profile.isProfileComplete ? (
                      <FaCheckCircle className="text-success" />
                    ) : (
                      <FaExclamationTriangle className="text-warning" />
                    )}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Status & Subscription</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Active:</strong> {profile.isActive ? "Yes" : "No"}
                  </p>
                  <p>
                    <strong>Subscription:</strong>{" "}
                    <Badge
                      bg={
                        profile.subscription === "premium"
                          ? "success"
                          : profile.subscription === "basic"
                            ? "primary"
                            : "secondary"
                      }
                    >
                      {typeof profile.subscription === "object" &&
                        profile.subscription !== null
                        ? profile.subscription.plan
                        : profile.subscription}
                    </Badge>
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Created At:</strong>{" "}
                    {profile.createdAt
                      ? new Date(profile.createdAt).toLocaleString()
                      : "—"}
                  </p>
                  <p>
                    <strong>Updated At:</strong>{" "}
                    {profile.updatedAt
                      ? new Date(profile.updatedAt).toLocaleString()
                      : "—"}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Referral</h5>
            </Card.Header>
            <Card.Body>
              <p className="mb-2">
                <strong>Referral Code:</strong> {profile.referralCode}
              </p>
              <p className="mb-2">
                <strong>Referral Points:</strong> {profile.referralPoints}
              </p>
              <p className="mb-0">
                <strong>Total Referrals:</strong> {profile.referralsCount}
              </p>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h5 className="mb-0">Account Status</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-2">
                <Badge bg={profile.isActive ? "success" : "secondary"}>
                  {profile.isActive ? "ACTIVE" : "INACTIVE"}
                </Badge>
              </div>
              <div>
                <Badge bg={profile.isProfileComplete ? "success" : "warning"}>
                  {profile.isProfileComplete
                    ? "PROFILE COMPLETE"
                    : "INCOMPLETE PROFILE"}
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserProfile;
